/**
 * Ad Tech Navigator - Interactive Value Chain Map
 *
 * Visualizes the performance advertising ecosystem with:
 * - Layered columns: MMPs, Walled Gardens, Ad Networks, DSPs, Mediation, PC Discovery
 * - Company cards with market share and power ratings
 * - Click-to-expand detail popups with deep dive content
 * - Filter by business type (Hypercasual, Casual, Midcore, PC, Subscription)
 *
 * Data sourced from Transcend internal research
 */
import { useState, useMemo } from 'react';
import {
  FiX, FiArrowRight, FiStar, FiChevronDown, FiChevronUp, FiAlertCircle,
  FiZap, FiSmartphone, FiMonitor, FiCreditCard, FiTarget, FiActivity,
  FiTrendingUp, FiBarChart2, FiDatabase, FiLayers, FiGrid,
  FiUsers, FiGlobe, FiVideo, FiMessageCircle, FiSearch,
  FiRadio, FiCpu, FiShare2, FiBox, FiPlay
} from 'react-icons/fi';
import './AdTechNavigator.css';

// ============================================================================
// AD TECH DATA MODEL - From Andrew's Transcend Research
// ============================================================================

const BUSINESS_TYPES = [
  { id: 'hypercasual', label: 'Hypercasual', IconComponent: FiZap, color: '#8b5cf6' },
  { id: 'casual', label: 'Mobile Casual', IconComponent: FiSmartphone, color: '#3b82f6' },
  { id: 'midcore', label: 'Mobile Midcore', IconComponent: FiTarget, color: '#10b981' },
  { id: 'pc', label: 'PC Games', IconComponent: FiMonitor, color: '#f59e0b' },
  { id: 'subscription', label: 'Subscription Apps', IconComponent: FiCreditCard, color: '#ec4899' },
];

const CATEGORIES = [
  { id: 'mmp', label: 'Measurement (MMPs)', color: '#8b5cf6', order: 1, description: 'Track attribution & measure campaign performance', IconComponent: FiActivity },
  { id: 'walled-garden', label: 'Walled Gardens', color: '#2c5aa0', order: 2, description: 'Self-contained ad platforms with proprietary data', IconComponent: FiGlobe },
  { id: 'ad-network', label: 'Ad Networks', color: '#27ae60', order: 3, description: 'SDK-based networks for games & apps', IconComponent: FiShare2 },
  { id: 'dsp', label: 'DSPs', color: '#f59e0b', order: 4, description: 'Programmatic buying platforms', IconComponent: FiCpu },
  { id: 'mediation', label: 'Mediation', color: '#dc2626', order: 5, description: 'Monetization platforms aggregating demand', IconComponent: FiLayers },
  { id: 'pc-discovery', label: 'PC Discovery', color: '#6366f1', order: 6, description: 'Discovery platforms for PC games', IconComponent: FiMonitor },
];

// Icon mapping for platforms
const PLATFORM_ICONS = {
  appsflyer: FiBarChart2,
  adjust: FiTrendingUp,
  singular: FiTarget,
  kochava: FiDatabase,
  meta: FiUsers,
  google: FiSearch,
  tiktok: FiVideo,
  'apple-search-ads': FiSearch,
  snap: FiRadio,
  applovin: FiBox,
  unity: FiPlay,
  mintegral: FiGlobe,
  pangle: FiGlobe,
  liftoff: FiTrendingUp,
  inmobi: FiSmartphone,
  chartboost: FiBarChart2,
  moloco: FiCpu,
  remerge: FiShare2,
  smadex: FiRadio,
  'applovin-max': FiLayers,
  'unity-levelplay': FiTarget,
  admob: FiGrid,
  steam: FiPlay,
  twitch: FiVideo,
  discord: FiMessageCircle,
  gamesight: FiTarget,
  adzap: FiZap,
};

// Ad Tech Platforms Data - Comprehensive from Transcend Research
const AD_TECH_PLATFORMS = {
  // ==================== MMPs ====================
  appsflyer: {
    name: 'AppsFlyer',
    layer: 'mmp',
    icon: '📊',
    marketShare: { hypercasual: 40, casual: 45, midcore: 45, pc: 0, subscription: 40 },
    oneLiner: 'The dominant MMP for mobile gaming with the deepest integrations',
    powerRating: { volume: 5, quality: 4 },
    deepDive: {
      overview: `AppsFlyer is the market leader in mobile measurement and attribution, processing over 8 billion daily events. Founded in 2011, they pioneered mobile attribution and have maintained dominance through aggressive integration partnerships and reliable data.

Their SDK is integrated into thousands of games and apps, providing the industry's most comprehensive view of the mobile advertising ecosystem. They offer probabilistic and deterministic attribution, fraud protection (Protect360), and advanced analytics.`,
      vcPerspective: `AppsFlyer is the safe choice - rarely wrong to use them. Their main drawback is cost at scale. For portfolio companies spending under $500K/month, the reliability and integrations justify the premium. Above that, evaluate Adjust or Singular for potential savings.`,
      pros: ['Industry standard - every network integrates', 'Best fraud detection (Protect360)', 'Excellent customer support', 'Most comprehensive integrations'],
      cons: ['Most expensive MMP option', 'Complex pricing tiers', 'Can be overkill for small spend', 'Data ownership concerns'],
      integrationDifficulty: 3,
      pricingModel: 'Per-attributed-install pricing with tiered rates. Expect $0.03-0.08 per install at scale. Enterprise deals available.',
      bestFor: ['hypercasual', 'casual', 'midcore', 'subscription'],
      upstream: 'Receives postback data from all ad networks and walled gardens',
      downstream: 'Sends conversion data back to ad platforms for optimization',
      competitors: ['adjust', 'singular', 'kochava'],
      transcendAlpha: 'Portfolio companies report 15-20% higher attributed ROAS vs Adjust due to better probabilistic matching. Worth the premium for games with complex UA funnels.'
    }
  },
  adjust: {
    name: 'Adjust',
    layer: 'mmp',
    icon: '📈',
    marketShare: { hypercasual: 35, casual: 35, midcore: 35, pc: 0, subscription: 35 },
    oneLiner: 'Strong #2 MMP with excellent privacy-first features and competitive pricing',
    powerRating: { volume: 4, quality: 4 },
    deepDive: {
      overview: `Adjust, acquired by AppLovin in 2021, is the second-largest MMP globally. They've positioned themselves as the privacy-first alternative, with strong SKAdNetwork support and innovative solutions for the post-IDFA world.

Their acquisition by AppLovin creates potential conflicts of interest but also ensures long-term investment in the product. They offer Fraud Prevention Suite, Audience Builder, and comprehensive analytics.`,
      vcPerspective: `Good alternative to AppsFlyer, especially for companies in the AppLovin ecosystem. The AppLovin ownership can be a pro (tighter integration) or con (competitor data concerns) depending on your UA mix.`,
      pros: ['20-30% cheaper than AppsFlyer', 'Excellent privacy compliance', 'Strong SKAdNetwork support', 'Good for AppLovin-heavy UA'],
      cons: ['AppLovin ownership creates conflicts', 'Smaller integration network', 'Less comprehensive fraud detection', 'Support can be slower'],
      integrationDifficulty: 3,
      pricingModel: 'Similar per-install model to AppsFlyer but typically 20-30% lower rates. Volume discounts available.',
      bestFor: ['hypercasual', 'casual', 'midcore', 'subscription'],
      upstream: 'Receives data from ad networks, with tightest integration to AppLovin',
      downstream: 'Attribution data flows to ad platforms for campaign optimization',
      competitors: ['appsflyer', 'singular', 'kochava'],
      transcendAlpha: 'If >50% of your spend is on AppLovin, Adjust makes sense. Otherwise, AppsFlyer\'s broader ecosystem usually wins.'
    }
  },
  singular: {
    name: 'Singular',
    layer: 'mmp',
    icon: '🎯',
    marketShare: { hypercasual: 15, casual: 10, midcore: 12, pc: 0, subscription: 15 },
    oneLiner: 'Cost-effective MMP with strong analytics and cost aggregation',
    powerRating: { volume: 3, quality: 4 },
    deepDive: {
      overview: `Singular differentiates through their marketing analytics platform that goes beyond attribution. They aggregate costs from all sources, provide creative analytics, and offer a unified view of marketing performance.

Particularly strong for companies that want to consolidate their analytics stack. Their cost aggregation automatically pulls spend data from 1000+ sources, eliminating manual reporting.`,
      vcPerspective: `Best for data-mature teams who want analytics consolidation. Less suitable for early-stage companies who just need basic attribution. The analytics value-add justifies switching from AppsFlyer for some midcore studios.`,
      pros: ['Excellent cost aggregation', 'Strong creative analytics', 'Competitive pricing', 'Good for consolidated reporting'],
      cons: ['Smaller market presence', 'Fewer integrations than leaders', 'Less brand recognition', 'Support network smaller'],
      integrationDifficulty: 3,
      pricingModel: 'Competitive pricing with analytics bundled. Often 30-40% cheaper than AppsFlyer for equivalent features.',
      bestFor: ['midcore', 'subscription'],
      upstream: 'Aggregates data from all ad platforms and organic sources',
      downstream: 'Provides unified analytics and attribution data',
      competitors: ['appsflyer', 'adjust', 'kochava'],
      transcendAlpha: 'Consider Singular when your marketing team is drowning in spreadsheets. The cost aggregation alone can save 10+ hours/week in manual reporting.'
    }
  },
  kochava: {
    name: 'Kochava',
    layer: 'mmp',
    icon: '🔬',
    marketShare: { hypercasual: 5, casual: 5, midcore: 8, pc: 5, subscription: 10 },
    oneLiner: 'Enterprise MMP with advanced features and PC/console capabilities',
    powerRating: { volume: 3, quality: 4 },
    deepDive: {
      overview: `Kochava offers the most comprehensive feature set of any MMP, including unique capabilities for PC and console measurement. They're the only MMP with native support for Steam and console platforms.

Their Marketers Operating System (m/OS) provides identity resolution, audience management, and advanced analytics. Best suited for sophisticated marketing teams.`,
      vcPerspective: `Kochava is the choice for PC games and multiplatform releases. For mobile-only, it's usually overkill. Their complexity means higher implementation costs but more flexibility.`,
      pros: ['PC and console support', 'Most advanced feature set', 'Strong identity resolution', 'Good for multiplatform'],
      cons: ['Complex implementation', 'Steeper learning curve', 'Higher total cost of ownership', 'Overkill for simple use cases'],
      integrationDifficulty: 4,
      pricingModel: 'Enterprise pricing model. Expect higher upfront costs but more flexibility in contract structure.',
      bestFor: ['pc', 'midcore', 'subscription'],
      upstream: 'Connects to ad platforms across mobile, PC, console, and web',
      downstream: 'Provides cross-platform unified attribution',
      competitors: ['appsflyer', 'adjust', 'singular'],
      transcendAlpha: 'For PC games or multiplatform releases, Kochava is the only real option. Budget 2-3x the implementation time vs mobile-only MMPs.'
    }
  },

  // ==================== WALLED GARDENS ====================
  meta: {
    name: 'Meta (Facebook/Instagram)',
    layer: 'walled-garden',
    icon: '👤',
    marketShare: { hypercasual: 5, casual: 25, midcore: 25, pc: 10, subscription: 40 },
    oneLiner: 'Largest social advertising platform with unmatched targeting and scale',
    powerRating: { volume: 5, quality: 4 },
    deepDive: {
      overview: `Meta's advertising platform spans Facebook, Instagram, Messenger, and the Audience Network. With 3+ billion monthly active users, they offer unparalleled reach and sophisticated targeting.

Their machine learning algorithms are industry-leading, especially for app install campaigns. The Aggregated Event Measurement (AEM) and Conversions API (CAPI) help navigate iOS privacy changes.`,
      vcPerspective: `Meta works for hybrid casual with IAP revenue but is too expensive for pure hypercasual CPI arbitrage. Essential for midcore and subscription apps. Best for broad audiences and games with strong monetization.`,
      pros: ['Massive scale and reach', 'Best-in-class ML targeting', 'Strong creative optimization', 'Good for broad audiences'],
      cons: ['Too expensive for hypercasual', 'iOS measurement challenges', 'Volatile performance', 'High minimum viable spend'],
      integrationDifficulty: 2,
      pricingModel: 'Auction-based. Mobile game CPIs typically $2-10 for casual, $5-20 for midcore. Highly competitive.',
      bestFor: ['casual', 'midcore', 'subscription'],
      upstream: 'Direct buy - no intermediaries needed',
      downstream: 'Serves ads across FB, IG, Messenger, Audience Network',
      competitors: ['google', 'tiktok', 'snap'],
      transcendAlpha: 'Post-ATT, we see best Meta performance with: broad targeting (let ML work), strong creative testing (5+ variants weekly), and high event optimization (purchase > install).'
    }
  },
  google: {
    name: 'Google (UAC/YouTube)',
    layer: 'walled-garden',
    icon: '🔍',
    marketShare: { hypercasual: 3, casual: 15, midcore: 40, pc: 15, subscription: 30 },
    oneLiner: 'Best-in-class for IAP whales. Inefficient for ad-revenue optimization.',
    powerRating: { volume: 5, quality: 5 },
    hasWarning: true,
    warningText: 'Cannot optimize for ad revenue - only IAP purchases',
    deepDive: {
      overview: `Google's Universal App Campaigns (UAC) automate ad delivery across Search, Play Store, YouTube, Display Network, and Discover. Their access to Play Store data provides unique optimization signals for purchase behavior.

⚠️ ALGORITHMIC LIMITATION: Google's AI is architected to find IAP "whales" - users with high purchase intent. It fundamentally cannot optimize for distributed ad revenue accumulation (users watching 30 ads over 7 days). This is an algorithmic design choice, not a "learning problem" that more spend will fix.`,
      vcPerspective: `Google is the engine room for IAP-focused Midcore and Subscription apps due to superior purchase prediction. CAUTION: Do not prioritize Google for ad-monetized (Hypercasual) portfolios. The algorithm struggles to optimize for ad-view volume vs. purchase value.`,
      pros: ['Best for finding IAP purchasers', 'Play Store integration', 'Best for Android payers', 'Strong intent signals'],
      cons: ['Cannot optimize for ad revenue', 'Ineffective for hypercasual', 'Black-box optimization', 'Requires IAP events to optimize'],
      integrationDifficulty: 2,
      pricingModel: 'tCPA/tROAS bidding. Budget minimums effectively $10K+/month for meaningful data. Games see CPIs of $1-15 depending on category.',
      bestFor: ['midcore', 'subscription'],
      upstream: 'Direct buy through Google Ads',
      downstream: 'Serves across Search, Play Store, YouTube, Display, Discover',
      competitors: ['meta', 'tiktok', 'apple-search-ads'],
      transcendAlpha: 'Google tROAS is gold standard for IAP, but fails at Ad ROAS. If your game relies on ads for >70% of revenue, move budget to AppLovin or Mintegral. For Midcore: Feed it deep-funnel purchase events (min 50/week).'
    }
  },
  tiktok: {
    name: 'TikTok',
    layer: 'walled-garden',
    icon: '🎵',
    marketShare: { hypercasual: 10, casual: 5, midcore: 5, pc: 5, subscription: 15 },
    oneLiner: 'Fastest-growing ad platform with unique creative format and young audience',
    powerRating: { volume: 4, quality: 3 },
    deepDive: {
      overview: `TikTok for Business has exploded as an advertising platform, offering access to a highly engaged, predominantly young audience. Their full-screen vertical video format demands unique creative approaches.

The platform rewards authenticity and native-feeling content. User-generated content (UGC) style ads typically outperform polished productions. Works for hypercasual when CPIs stay low.`,
      vcPerspective: `TikTok is essential for reaching Gen Z and younger millennials. Creative is everything here - you need TikTok-native content, not repurposed Facebook ads. High variance but can unlock new audiences.`,
      pros: ['Access to young demographics', 'High engagement rates', 'Creative innovation potential', 'Can work for hypercasual'],
      cons: ['Requires native creative', 'Volatile performance', 'Less mature optimization', 'Brand safety concerns'],
      integrationDifficulty: 2,
      pricingModel: 'Auction-based with CPI and CPA goals. CPIs typically $1-8 for casual games. Creative refresh required frequently.',
      bestFor: ['hypercasual', 'casual', 'subscription'],
      upstream: 'Direct buy through TikTok Ads Manager',
      downstream: 'Serves across TikTok and Pangle (their audience network)',
      competitors: ['meta', 'google', 'snap'],
      transcendAlpha: 'TikTok creative burns out fast - plan for weekly refreshes. UGC-style content from real creators outperforms studio creative 3:1 in our portfolio.'
    }
  },
  'apple-search-ads': {
    name: 'Apple Search Ads',
    layer: 'walled-garden',
    icon: '🍎',
    marketShare: { hypercasual: 2, casual: 10, midcore: 15, pc: 0, subscription: 30 },
    oneLiner: 'Premium iOS traffic with highest intent and best attribution post-ATT',
    powerRating: { volume: 3, quality: 5 },
    deepDive: {
      overview: `Apple Search Ads places your app at the top of App Store search results. As a first-party platform, it has the most reliable attribution on iOS post-ATT and accesses high-intent users actively searching for apps.

Two products: Search Ads Basic (automated, simple) and Search Ads Advanced (keyword control, audiences). Most serious advertisers use Advanced.`,
      vcPerspective: `ASA is the most reliable iOS channel post-ATT. Quality is consistently high but scale is limited. Use it as your iOS foundation and supplement with other channels for growth.`,
      pros: ['Highest iOS quality', 'Best attribution post-ATT', 'High intent users', 'Reliable performance'],
      cons: ['Limited scale', 'iOS only', 'Competitive keywords expensive', 'Less creative flexibility'],
      integrationDifficulty: 1,
      pricingModel: 'CPA/CPT bidding. Brand keywords $1-3 CPA, category keywords $5-20+. Competition drives prices significantly.',
      bestFor: ['midcore', 'subscription'],
      upstream: 'Direct buy through Apple Ads platform',
      downstream: 'Serves in App Store search results and Today tab',
      competitors: ['meta', 'google'],
      transcendAlpha: 'ASA is your iOS baseline. We see 2-3x better retention from ASA users vs other iOS channels. Max it out before scaling elsewhere.'
    }
  },
  snap: {
    name: 'Snapchat',
    layer: 'walled-garden',
    icon: '👻',
    marketShare: { hypercasual: 5, casual: 8, midcore: 5, pc: 0, subscription: 10 },
    oneLiner: 'Strong with younger demographics and AR-focused creative',
    powerRating: { volume: 3, quality: 3 },
    deepDive: {
      overview: `Snapchat Ads offers access to a unique, younger demographic with strong engagement in AR experiences. Their AR lenses and filters provide creative opportunities other platforms can't match.

The platform has invested heavily in direct response capabilities and now offers competitive optimization for app installs and purchases.`,
      vcPerspective: `Snap is a solid secondary channel for games targeting younger audiences. AR lens campaigns can drive massive awareness but conversion is harder. Test it after maxing Meta/Google.`,
      pros: ['Young demographic reach', 'Unique AR capabilities', 'Lower competition', 'Good CPIs for casual'],
      cons: ['Limited scale', 'Younger = lower LTV often', 'AR ads complex to produce', 'Measurement challenges'],
      integrationDifficulty: 2,
      pricingModel: 'Auction-based. CPIs typically $1-5 for casual games, competitive with TikTok.',
      bestFor: ['casual', 'subscription'],
      upstream: 'Direct buy through Snap Ads Manager',
      downstream: 'Serves across Snapchat app and Snap Audience Network',
      competitors: ['tiktok', 'meta'],
      transcendAlpha: 'Snap works best for games with strong social/sharing mechanics. The audience over-indexes on social features.'
    }
  },

  // ==================== AD NETWORKS ====================
  applovin: {
    name: 'AppLovin',
    layer: 'ad-network',
    icon: '🦁',
    marketShare: { hypercasual: 40, casual: 30, midcore: 15, pc: 0, subscription: 10 },
    oneLiner: 'Dominant mobile ad network - essential for ad-monetized games',
    powerRating: { volume: 5, quality: 5 },
    isEssential: true,
    deepDive: {
      overview: `AppLovin is the 800-pound gorilla of mobile gaming advertising. Their AXON machine learning engine processes billions of events and crucially, can optimize for ad revenue - not just IAP. They own MAX, the leading mediation platform.

The AppLovin ecosystem is self-reinforcing: games using MAX for monetization feed data to AXON for better acquisition targeting. This flywheel makes them ESSENTIAL for hypercasual and ad-monetized games.`,
      vcPerspective: `AppLovin is THE network for hypercasual and ad-monetized games. While Google struggles to optimize for ad views, AppLovin's SDK network knows exactly who watches ads. For hypercasual, 30-50% of spend. For casual with ads, still 20-30%.`,
      pros: ['Massive scale for games', 'Best-in-class ML (AXON)', 'MAX mediation integration', 'Strong for casual/mid-casual'],
      cons: ['Can be expensive', 'Less effective for non-games', 'Walled garden behavior', 'Requires significant spend'],
      integrationDifficulty: 3,
      pricingModel: 'CPI and ROAS-based campaigns. CPIs $0.20-0.50 for hypercasual, $1-5 for casual, higher for midcore. ROAS campaigns increasingly preferred.',
      bestFor: ['hypercasual', 'casual', 'midcore'],
      upstream: 'Buys through owned inventory and programmatic exchanges',
      downstream: 'Serves ads in thousands of mobile games via MAX',
      competitors: ['unity', 'mintegral', 'pangle'],
      transcendAlpha: 'For ad-monetized games, AppLovin > Google. Period. Their SDK footprint means they know who watches ads. Start with automatic ROAS campaigns, manual only after 10K+ installs.'
    }
  },
  unity: {
    name: 'Unity (LevelPlay/ironSource)',
    layer: 'ad-network',
    icon: '🎮',
    marketShare: { hypercasual: 20, casual: 15, midcore: 10, pc: 0, subscription: 5 },
    oneLiner: 'Game-focused ad network with strong rewarded video and playables',
    powerRating: { volume: 4, quality: 4 },
    deepDive: {
      overview: `Unity Ads, enhanced by the ironSource acquisition, is the second-largest mobile gaming ad network. Their deep integration with the Unity game engine provides unique advantages for Unity-built games.

LevelPlay (formerly ironSource mediation) competes directly with MAX. The combined entity offers end-to-end solutions from game development to monetization to UA.`,
      vcPerspective: `Unity is the natural choice for Unity-built games. Even for non-Unity games, they're a strong #2 network. Use them alongside AppLovin for diversification and competitive bidding.`,
      pros: ['Deep Unity engine integration', 'Strong for playable ads', 'LevelPlay mediation', 'Game-specific optimization'],
      cons: ['Weaker outside gaming', 'Smaller than AppLovin', 'ironSource integration ongoing', 'Creative requirements'],
      integrationDifficulty: 3,
      pricingModel: 'CPI and ROAS campaigns. Generally comparable to AppLovin pricing.',
      bestFor: ['hypercasual', 'casual', 'midcore'],
      upstream: 'Buys through Unity ecosystem and exchanges',
      downstream: 'Serves in Unity-built games and broader mobile inventory',
      competitors: ['applovin', 'mintegral', 'pangle'],
      transcendAlpha: 'If your game is built in Unity, their SDK integration data makes targeting 10-15% more efficient. Key secondary network for hypercasual after AppLovin.'
    }
  },
  mintegral: {
    name: 'Mintegral',
    layer: 'ad-network',
    icon: '🌏',
    marketShare: { hypercasual: 15, casual: 10, midcore: 5, pc: 0, subscription: 3 },
    oneLiner: 'Rising star for hypercasual with competitive CPIs and strong scale',
    powerRating: { volume: 4, quality: 3 },
    deepDive: {
      overview: `Mintegral is a leading ad network with particular strength in APAC markets and hypercasual games. They offer highly competitive CPIs - often 20-30% below AppLovin - making them essential for CPI arbitrage models.

Their programmatic exchange provides transparency, and their SDK footprint in hypercasual games means strong ad-revenue optimization signals.`,
      vcPerspective: `Mintegral is a rising star for hypercasual. Their CPIs make them essential for games running tight CPI arbitrage. Quality can be variable - monitor cohort ROAS closely - but they're now a tier-1 option for ad-monetized games.`,
      pros: ['Competitive CPIs', 'Strong in APAC', 'Good scale', 'Programmatic transparency'],
      cons: ['Quality variance', 'Less sophisticated ML', 'Fraud monitoring needed', 'Support can be slow'],
      integrationDifficulty: 2,
      pricingModel: 'Typically 20-30% lower CPIs than AppLovin. Good for volume at lower costs.',
      bestFor: ['hypercasual', 'casual'],
      upstream: 'Operates exchange and direct buys',
      downstream: 'Serves across mobile inventory with APAC strength',
      competitors: ['applovin', 'unity', 'pangle'],
      transcendAlpha: 'For hypercasual, test Mintegral early - their CPIs can make tight arbitrage work. Watch fraud metrics closely with Protect360.'
    }
  },
  pangle: {
    name: 'Pangle',
    layer: 'ad-network',
    icon: '🐼',
    marketShare: { hypercasual: 12, casual: 5, midcore: 3, pc: 0, subscription: 2 },
    oneLiner: "TikTok's audience network - massive scale in APAC and emerging markets",
    powerRating: { volume: 5, quality: 3 },
    deepDive: {
      overview: `Pangle is the ad network of TikTok for Business, allowing advertisers to extend TikTok campaigns to thousands of third-party apps. It is DOMINANT in Asia (Japan, Korea, SEA) and growing rapidly in LATAM/MENA.

For Hypercasual games, Pangle often rivals AppLovin and Mintegral for volume, specifically for rewarded video placements. Their scale in Tier 2/3 markets is unmatched.`,
      vcPerspective: `Essential for any game with a global strategy. If you are launching Hypercasual, Pangle is a Tier-1 partner alongside AppLovin and Mintegral. Don't ignore it just because it's not strong in the US yet.`,
      pros: ['Massive APAC scale', 'High fill rates', 'TikTok creative assets work here', 'Fast growing'],
      cons: ['Weaker in US/Tier 1 West', 'Quality can be lower than AppLovin', 'Heavily skewed to Android', 'Requires geographic strategy'],
      integrationDifficulty: 2,
      pricingModel: 'Very competitive CPIs, often lower than Mintegral in Tier 2/3 geos. Strong for volume at low cost.',
      bestFor: ['hypercasual', 'casual'],
      upstream: 'TikTok Ads Manager',
      downstream: 'Thousands of apps (mostly gaming) in APAC/LATAM',
      competitors: ['mintegral', 'applovin', 'unity'],
      transcendAlpha: 'Reuse your top-performing TikTok UGC creatives on Pangle. The native format fits perfectly and requires zero extra production cost. Essential for APAC expansion.'
    }
  },
  liftoff: {
    name: 'Liftoff (Vungle)',
    layer: 'ad-network',
    icon: '🚀',
    marketShare: { hypercasual: 5, casual: 8, midcore: 10, pc: 0, subscription: 8 },
    oneLiner: 'Programmatic-first network strong in midcore and subscription apps',
    powerRating: { volume: 3, quality: 4 },
    deepDive: {
      overview: `Liftoff, which acquired Vungle, combines programmatic buying expertise with game-focused inventory. They're particularly strong for midcore games and subscription apps where LTV optimization matters more than volume.

Their ML focuses on downstream events (revenue, retention) rather than just installs, making them valuable for quality-focused campaigns.`,
      vcPerspective: `Liftoff is underrated for midcore. Their LTV-focused optimization often delivers better D30 ROAS than higher-volume networks. Test them for your paying user campaigns.`,
      pros: ['LTV-focused optimization', 'Good for midcore', 'Subscription app strength', 'Quality over volume'],
      cons: ['Lower scale', 'Less competitive for casual', 'Requires longer optimization', 'Higher minimum budgets'],
      integrationDifficulty: 2,
      pricingModel: 'CPA/ROAS focused. Higher CPIs but often better downstream metrics.',
      bestFor: ['casual', 'midcore', 'subscription'],
      upstream: 'Programmatic buying across exchanges',
      downstream: 'Access to premium mobile inventory',
      competitors: ['applovin', 'unity', 'mintegral'],
      transcendAlpha: 'For games with $20+ D30 ARPU, Liftoff often outperforms AppLovin on ROAS despite higher CPIs. Not cost-effective for hypercasual.'
    }
  },
  inmobi: {
    name: 'InMobi',
    layer: 'ad-network',
    icon: '📱',
    marketShare: { hypercasual: 3, casual: 5, midcore: 5, pc: 0, subscription: 5 },
    oneLiner: 'Global network with strength in emerging markets and programmatic',
    powerRating: { volume: 3, quality: 3 },
    deepDive: {
      overview: `InMobi is one of the largest independent mobile ad networks globally, with particular strength in India and emerging markets. They offer both managed and programmatic buying options.

Their Glance product powers lock screen content for millions of devices, providing unique inventory access.`,
      vcPerspective: `InMobi is useful for emerging market expansion. For US/Europe focus, they're a tier-2 option. Test them when you need geographic diversification.`,
      pros: ['Strong in emerging markets', 'Unique lock screen inventory', 'Competitive pricing', 'Good reach'],
      cons: ['Quality varies by geo', 'Less game-specific', 'Smaller scale in West', 'Support inconsistent'],
      integrationDifficulty: 2,
      pricingModel: 'Competitive CPIs, particularly strong value in emerging markets.',
      bestFor: ['casual', 'subscription'],
      upstream: 'Direct and programmatic buying',
      downstream: 'Global mobile inventory with emerging market strength',
      competitors: ['mintegral', 'liftoff'],
      transcendAlpha: 'InMobi is your emerging markets play. If you\'re expanding to India/SEA, start here. For US-only, deprioritize.'
    }
  },
  chartboost: {
    name: 'Chartboost',
    layer: 'ad-network',
    icon: '📊',
    marketShare: { hypercasual: 5, casual: 5, midcore: 5, pc: 0, subscription: 2 },
    oneLiner: 'Game-focused network with strong direct publisher relationships',
    powerRating: { volume: 2, quality: 3 },
    deepDive: {
      overview: `Chartboost, owned by Zynga (and now Take-Two), focuses exclusively on mobile games. They have strong direct relationships with game publishers and offer both SDK and programmatic access.

Their specialization in games means inventory is always gaming-relevant, reducing wasted spend on non-game placements.`,
      vcPerspective: `Chartboost is a solid third-tier network for games. Their game-only inventory is valuable but scale is limited. Include them in your mediation but don't expect them to be a primary driver.`,
      pros: ['Games-only inventory', 'Direct publisher deals', 'Good quality floor', 'Simple integration'],
      cons: ['Limited scale', 'Zynga ownership concerns', 'Less sophisticated ML', 'Declining market share'],
      integrationDifficulty: 2,
      pricingModel: 'Competitive CPIs within gaming inventory. Good for incremental reach.',
      bestFor: ['hypercasual', 'casual', 'midcore'],
      upstream: 'Direct publisher relationships',
      downstream: 'Games-only mobile inventory',
      competitors: ['applovin', 'unity', 'pangle'],
      transcendAlpha: 'Chartboost is declining but their game-focused inventory still delivers. Use them for incremental reach, not as a primary driver.'
    }
  },

  // ==================== DSPs ====================
  moloco: {
    name: 'Moloco',
    layer: 'dsp',
    icon: '🤖',
    marketShare: { hypercasual: 2, casual: 8, midcore: 15, pc: 5, subscription: 15 },
    oneLiner: 'Leading independent DSP with strong ML and transparency',
    powerRating: { volume: 4, quality: 5 },
    deepDive: {
      overview: `Moloco is the most sophisticated independent DSP for mobile performance advertising. Their machine learning, built by ex-Google engineers, rivals the walled gardens in effectiveness.

They offer full transparency into bidding, placements, and optimization - something the walled gardens and SDK networks don't provide. This makes them valuable for teams who want control.`,
      vcPerspective: `Moloco is increasingly essential for sophisticated UA. They outperform SDK networks for midcore and high-LTV apps. Worth the complexity for teams ready to graduate from automatic campaigns.`,
      pros: ['Best-in-class ML', 'Full transparency', 'Strong for high-LTV', 'Growing rapidly'],
      cons: ['Requires sophistication', 'Higher minimum spend', 'Less game-specific', 'Steeper learning curve'],
      integrationDifficulty: 4,
      pricingModel: 'Percentage of spend model. Typically 15-25% of media cost as platform fee.',
      bestFor: ['midcore', 'subscription', 'pc'],
      upstream: 'Programmatic buying across all major exchanges',
      downstream: 'Access to programmatic mobile inventory globally',
      competitors: ['remerge', 'smadex', 'kayzen'],
      transcendAlpha: 'Moloco shines when you have 90+ days of cohort data to feed their ML. Start with walled gardens, graduate to Moloco when you have data maturity.'
    }
  },
  remerge: {
    name: 'Remerge',
    layer: 'dsp',
    icon: '🔄',
    marketShare: { hypercasual: 0, casual: 3, midcore: 8, pc: 0, subscription: 10 },
    oneLiner: 'Retargeting specialist with strong app re-engagement capabilities',
    powerRating: { volume: 3, quality: 4 },
    deepDive: {
      overview: `Remerge specializes in app retargeting and re-engagement. While most DSPs focus on user acquisition, Remerge helps you bring back lapsed users and convert free users to paying.

Their focus on retargeting means deep expertise in re-engagement creative, frequency capping, and incrementality measurement.`,
      vcPerspective: `Remerge is the right partner when retention is your bottleneck, not acquisition. For games with strong D1 but weak D30, test re-engagement campaigns before scaling UA.`,
      pros: ['Retargeting expertise', 'Incrementality focus', 'Good for re-engagement', 'Strong creative tools'],
      cons: ['Limited to retargeting', 'Requires existing users', 'Scale limited by audience', 'Not for new games'],
      integrationDifficulty: 3,
      pricingModel: 'Performance-based on re-engagement events. CPA/ROAS optimization available.',
      bestFor: ['midcore', 'subscription'],
      upstream: 'Retargets your existing user base across exchanges',
      downstream: 'Reaches lapsed users across mobile inventory',
      competitors: ['moloco', 'smadex'],
      transcendAlpha: 'Run Remerge re-engagement 6+ months after launch when you have a meaningful lapsed user base. Earlier is too small to be efficient.'
    }
  },
  smadex: {
    name: 'Smadex',
    layer: 'dsp',
    icon: '📡',
    marketShare: { hypercasual: 0, casual: 2, midcore: 5, pc: 0, subscription: 5 },
    oneLiner: 'Transparent DSP with strong European presence and competitive pricing',
    powerRating: { volume: 2, quality: 3 },
    deepDive: {
      overview: `Smadex offers a transparent programmatic platform with particular strength in European markets. They provide full visibility into bidding and placement data, appealing to data-driven teams.

Their pricing is competitive, making them attractive for cost-conscious advertisers willing to invest in optimization.`,
      vcPerspective: `Smadex is a good option for European expansion or teams wanting DSP transparency without Moloco's complexity. Test them as a secondary programmatic option.`,
      pros: ['Full transparency', 'Strong in Europe', 'Competitive pricing', 'Good for testing'],
      cons: ['Limited scale', 'Less sophisticated ML', 'Manual optimization needed', 'Smaller support team'],
      integrationDifficulty: 3,
      pricingModel: 'Transparent percentage of spend. Lower platform fees than Moloco.',
      bestFor: ['midcore', 'subscription'],
      upstream: 'Access to programmatic exchanges with European strength',
      downstream: 'Mobile inventory with European focus',
      competitors: ['moloco', 'remerge', 'kayzen'],
      transcendAlpha: 'Smadex for European campaigns, Moloco for global. Their European inventory access and local support add value for EMEA expansion.'
    }
  },

  // ==================== MEDIATION ====================
  'applovin-max': {
    name: 'AppLovin MAX',
    layer: 'mediation',
    icon: '🎰',
    marketShare: { hypercasual: 50, casual: 45, midcore: 40, pc: 0, subscription: 15 },
    oneLiner: 'Market-leading mediation platform with unmatched demand',
    powerRating: { volume: 5, quality: 5 },
    isEssential: true,
    deepDive: {
      overview: `MAX is the dominant mediation platform for mobile games, connecting your ad inventory to dozens of demand sources through a single SDK. Their in-app bidding technology maximizes revenue by running real-time auctions for every impression.

The MAX ecosystem includes advanced A/B testing, ad revenue analytics, and segment-based pricing. Their scale means they see more bid data than anyone, improving auction efficiency.`,
      vcPerspective: `MAX is the default choice for ad monetization in games. The only question is whether Unity LevelPlay's competitive positioning gives you better leverage. Most games should use MAX.`,
      pros: ['Largest demand pool', 'Best-in-class in-app bidding', 'Excellent analytics', 'Continuous optimization'],
      cons: ['AppLovin network favoritism concerns', 'Complex setup', 'Revenue share model', 'Lock-in risk'],
      integrationDifficulty: 3,
      pricingModel: 'Revenue share on ads served. Rates vary by tier but typically 5-15% of revenue.',
      bestFor: ['hypercasual', 'casual', 'midcore'],
      upstream: 'Aggregates demand from all major ad networks',
      downstream: 'Serves ads in your game, optimizing for revenue',
      competitors: ['unity-levelplay', 'admob'],
      transcendAlpha: 'MAX is essential for hypercasual monetization. The data flywheel (MAX monetization → AXON UA targeting) is unmatched. Monitor AppLovin network share - if it exceeds 50%, test Unity for competitive bidding.'
    }
  },
  'unity-levelplay': {
    name: 'Unity LevelPlay',
    layer: 'mediation',
    icon: '🎯',
    marketShare: { hypercasual: 35, casual: 35, midcore: 35, pc: 0, subscription: 10 },
    oneLiner: 'Strong MAX alternative with deep Unity integration',
    powerRating: { volume: 4, quality: 4 },
    deepDive: {
      overview: `LevelPlay (formerly ironSource mediation) is MAX's main competitor, offering similar in-app bidding and demand aggregation. Their Unity acquisition creates tight integration for Unity-built games.

The platform offers cross-promotion features, segment-based control, and detailed ad revenue analytics. The competitive tension with MAX benefits publishers through innovation.`,
      vcPerspective: `LevelPlay is the right choice if you want to avoid AppLovin dominance or have Unity-built games. Using both mediations in different games gives you leverage and diversification.`,
      pros: ['MAX alternative', 'Unity integration', 'Cross-promotion tools', 'Competitive pressure helps rates'],
      cons: ['Smaller demand pool', 'Integration still evolving', 'Less mature than MAX', 'Unity instability concerns'],
      integrationDifficulty: 3,
      pricingModel: 'Similar revenue share model to MAX. Competitive rates to win share.',
      bestFor: ['hypercasual', 'casual', 'midcore'],
      upstream: 'Aggregates demand from major ad networks',
      downstream: 'Serves optimized ads in your game',
      competitors: ['applovin-max', 'admob'],
      transcendAlpha: 'Test LevelPlay if MAX revenue plateaus. Competition between mediations can lift CPMs 10-20%. Strong choice for hypercasual wanting to avoid AppLovin concentration.'
    }
  },
  admob: {
    name: 'AdMob',
    layer: 'mediation',
    icon: '📺',
    marketShare: { hypercasual: 10, casual: 15, midcore: 10, pc: 0, subscription: 20 },
    oneLiner: "Google's mediation with strong non-gaming demand",
    powerRating: { volume: 3, quality: 3 },
    deepDive: {
      overview: `AdMob is Google's mobile ad platform, offering both direct ads and mediation. While less dominant in games than MAX/LevelPlay, their access to Google demand and non-gaming inventory makes them valuable for certain categories.

For subscription apps and games with broad audiences, AdMob's brand advertiser demand can drive higher CPMs than game-focused networks.`,
      vcPerspective: `AdMob is secondary for games but primary for non-gaming apps. If you have a subscription app or lifestyle-adjacent game, test AdMob's brand demand alongside gaming networks.`,
      pros: ['Google demand access', 'Strong for brands', 'Good for non-games', 'Reliable platform'],
      cons: ['Less game-specific', 'Lower CPMs for games', 'Less sophisticated mediation', 'Google lock-in'],
      integrationDifficulty: 2,
      pricingModel: 'Revenue share with access to Google demand.',
      bestFor: ['subscription'],
      upstream: 'Access to Google advertisers and network partners',
      downstream: 'Serves ads with brand advertiser strength',
      competitors: ['applovin-max', 'unity-levelplay'],
      transcendAlpha: 'AdMob shines for subscription apps and casual games with female demographics - brand advertisers pay premium CPMs for these audiences.'
    }
  },

  // ==================== PC DISCOVERY ====================
  steam: {
    name: 'Steam',
    layer: 'pc-discovery',
    icon: '🎮',
    marketShare: { hypercasual: 0, casual: 0, midcore: 0, pc: 60, subscription: 0 },
    oneLiner: 'Dominant PC game distribution with organic and paid discovery',
    powerRating: { volume: 5, quality: 5 },
    isEssential: true,
    deepDive: {
      overview: `Steam dominates PC game distribution with 120+ million monthly active users. Discovery happens through the store algorithm, wishlists, reviews, and paid featuring. The platform takes 30% revenue share (20% above $10M).

Steam's algorithm rewards engagement, reviews, and wishlist conversion. Building a wishlist before launch is the single most important marketing activity for PC games.`,
      vcPerspective: `For PC games, Steam IS your marketing strategy. Wishlist building, review management, and algorithm optimization matter more than paid advertising. Traditional UA doesn't work here.`,
      pros: ['Massive audience', 'Built-in discovery', 'Wishlist mechanics', 'Review credibility'],
      cons: ['30% revenue share', 'Algorithm dependency', 'Review vulnerability', 'Limited paid options'],
      integrationDifficulty: 2,
      pricingModel: '30% revenue share (20% above $10M). Paid featuring available for visibility.',
      bestFor: ['pc'],
      upstream: 'Direct sales platform - no intermediary needed',
      downstream: "Serves your game to Steam's 120M+ users",
      competitors: ['gamesight', 'adzap', 'twitch'],
      transcendAlpha: '10K wishlists before launch is the minimum for visibility. 50K+ for feature potential. Build your community and wishlist 6+ months before release.'
    }
  },
  twitch: {
    name: 'Twitch/YouTube Gaming',
    layer: 'pc-discovery',
    icon: '📺',
    marketShare: { hypercasual: 0, casual: 0, midcore: 0, pc: 30, subscription: 0 },
    oneLiner: 'Influencer and streaming platforms driving PC game discovery',
    powerRating: { volume: 4, quality: 4 },
    deepDive: {
      overview: `Twitch and YouTube Gaming are the primary discovery channels for PC games outside of Steam itself. Influencer coverage drives wishlists, and streaming creates ongoing engagement and awareness.

Campaigns range from paid sponsorships with major streamers to organic outreach to smaller creators. The key is finding authentic matches - forced sponsorships underperform.`,
      vcPerspective: `Influencer marketing is essential for PC launches but hard to scale. Focus on finding genuine advocates rather than paying for coverage. One authentic fan-streamer beats ten paid spots.`,
      pros: ['High trust', 'Drives wishlists', 'Long-term awareness', 'Authentic discovery'],
      cons: ['Hard to scale', 'Expensive top-tier', 'Results unpredictable', 'Requires relationship building'],
      integrationDifficulty: 3,
      pricingModel: 'CPM for ads, per-video for sponsorships. Major streamers $5K-50K+ per stream.',
      bestFor: ['pc'],
      upstream: 'Direct creator relationships or agency partnerships',
      downstream: 'Reaches gaming audiences through trusted voices',
      competitors: ['gamesight', 'adzap', 'discord'],
      transcendAlpha: 'Build relationships with 20+ mid-tier streamers (1K-10K viewers) rather than paying one big streamer. The cumulative authentic coverage outperforms.'
    }
  },
  discord: {
    name: 'Discord',
    layer: 'pc-discovery',
    icon: '💬',
    marketShare: { hypercasual: 0, casual: 0, midcore: 0, pc: 10, subscription: 0 },
    oneLiner: 'Community platform essential for PC game engagement',
    powerRating: { volume: 3, quality: 5 },
    deepDive: {
      overview: `Discord is where PC gaming communities live. While not a traditional advertising platform, your Discord server is essential for community building, feedback gathering, and word-of-mouth growth.

Active Discord communities correlate strongly with Steam success. They provide direct player access, beta testing pools, and organic advocacy.`,
      vcPerspective: `Discord is a leading indicator of PC game health. An engaged 1,000-member Discord often outperforms 10,000 disengaged followers elsewhere. Invest in community management.`,
      pros: ['Direct player access', 'Community building', 'Beta testing pool', 'Organic advocacy'],
      cons: ['Not paid advertising', 'Requires management', 'No direct attribution', 'Community moderation needed'],
      integrationDifficulty: 1,
      pricingModel: 'Free platform. Investment is in community management and content.',
      bestFor: ['pc'],
      upstream: 'Owned community platform',
      downstream: 'Drives word-of-mouth and Steam wishlists',
      competitors: ['gamesight', 'twitch', 'steam'],
      transcendAlpha: 'Discord engagement predicts Steam reviews. A 1,000-member active Discord typically translates to 200+ day-one reviews. Start building early.'
    }
  },
  gamesight: {
    name: 'Gamesight',
    layer: 'pc-discovery',
    icon: '🎯',
    marketShare: { hypercasual: 0, casual: 0, midcore: 0, pc: 15, subscription: 0 },
    oneLiner: 'Leading PC/console attribution and influencer marketing platform',
    powerRating: { volume: 4, quality: 5 },
    deepDive: {
      overview: `Gamesight is the dominant performance marketing platform for PC and console games, solving the attribution problem that has plagued the industry for years. They process 1.1B+ gaming data points daily and track conversions across Steam, Epic Games Store, PlayStation, Xbox, and Nintendo Switch.

Their platform combines marketing attribution with influencer campaign management, tracking creator content to actual sales and in-game purchases. With 129M+ creators in their database and 1,000+ games served globally, they're the closest thing to mobile-grade attribution for PC.`,
      vcPerspective: `Gamesight is essential for any serious PC UA operation. Before them, PC marketing was largely untrackable guesswork. Their attribution proves which influencers and campaigns actually drive wishlists and purchases. Worth the investment for games with real marketing budgets.`,
      pros: ['Real PC/console attribution', 'Influencer tracking to sales', 'Multi-platform support', 'Enterprise-grade (Ubisoft, Riot, Square Enix)'],
      cons: ['Enterprise pricing', 'Requires meaningful spend', 'Complex setup', 'Attribution still imperfect vs mobile'],
      integrationDifficulty: 3,
      pricingModel: 'Enterprise SaaS pricing. Scales with attributed installs and campaign volume. Customers report 70% CPA reduction ($17 to $5).',
      bestFor: ['pc'],
      upstream: 'Integrates with ad platforms, influencer campaigns, Steam/Epic APIs',
      downstream: 'Provides attribution data back to ad platforms and internal dashboards',
      competitors: ['adzap', 'steam'],
      transcendAlpha: "Gamesight is the MMP equivalent for PC. If you're spending $50K+/month on PC UA, you need attribution. Their influencer tracking alone can 3x your influencer ROI by cutting non-performers."
    }
  },
  adzap: {
    name: 'Adzap',
    layer: 'pc-discovery',
    icon: '⚡',
    marketShare: { hypercasual: 0, casual: 0, midcore: 0, pc: 8, subscription: 0 },
    oneLiner: 'Steam wishlist acquisition specialist with mobile-grade attribution',
    powerRating: { volume: 3, quality: 4 },
    deepDive: {
      overview: `Adzap is a performance marketing engine targeting PC/Console gamers, built by mobile publishing veterans with 500M+ installs and adtech engineers from major RTB exchanges. They raised pre-seed from a16z in July 2024.

Their core innovation is bringing mobile-grade attribution to Steam: they track wishlists at under $1 CPA and report 40%+ wishlist-to-purchase conversion rates. Their targeting uses first-party data enrichment to find users with gaming-related mobile apps (Steam, PlayStation, etc.) and serve them ads that drive wishlists.`,
      vcPerspective: `Adzap represents the mobile-ification of PC game marketing. Their team understands performance marketing at scale, and they're applying it to the PC attribution gap. Early but promising - good option for games that need to scale wishlist acquisition programmatically.`,
      pros: ['Sub-$1 wishlist acquisition', 'Mobile-to-PC attribution', 'View-through attribution', 'a16z backed'],
      cons: ['Early-stage company', 'Smaller scale than Gamesight', 'Less enterprise validation', 'Steam-focused (less console)'],
      integrationDifficulty: 2,
      pricingModel: 'Performance-based. Wishlist acquisition under $1, with 40%+ conversion to purchase at launch.',
      bestFor: ['pc'],
      upstream: 'Programmatic buying through RTB exchanges with gaming audience targeting',
      downstream: 'Drives Steam wishlists and launch conversions',
      competitors: ['gamesight', 'steam'],
      transcendAlpha: "Adzap is ideal for indie-to-mid-sized PC studios who need scale without Gamesight's enterprise complexity. Their sub-$1 wishlist costs are compelling if they hold at volume. Test alongside organic Steam efforts."
    }
  }
};

// Convert platforms object to array for easier manipulation
const AD_TECH_COMPANIES = Object.entries(AD_TECH_PLATFORMS).map(([id, data]) => ({
  id,
  ...data,
  category: data.layer // Map layer to category for compatibility
}));

// ============================================================================
// COMPONENT
// ============================================================================

function AdTechNavigator() {
  const [selectedBusinessType, setSelectedBusinessType] = useState('hypercasual');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Filter and sort companies by market share for selected business type
  const getCompaniesForCategory = useMemo(() => {
    return (categoryId) => {
      return AD_TECH_COMPANIES
        .filter(company => company.category === categoryId)
        .filter(company => {
          const share = company.marketShare?.[selectedBusinessType] || 0;
          return share > 0;
        })
        .sort((a, b) => {
          const shareA = a.marketShare?.[selectedBusinessType] || 0;
          const shareB = b.marketShare?.[selectedBusinessType] || 0;
          return shareB - shareA;
        });
    };
  }, [selectedBusinessType]);

  // Render star rating
  const renderStars = (volume, quality) => {
    return (
      <div className="power-rating">
        <div className="rating-row">
          <span className="rating-label">Vol</span>
          {[1, 2, 3, 4, 5].map(i => (
            <FiStar
              key={`vol-${i}`}
              className={`star ${i <= volume ? 'filled' : ''}`}
              size={12}
            />
          ))}
        </div>
        <div className="rating-row">
          <span className="rating-label">Qual</span>
          {[1, 2, 3, 4, 5].map(i => (
            <FiStar
              key={`qual-${i}`}
              className={`star ${i <= quality ? 'filled' : ''}`}
              size={12}
            />
          ))}
        </div>
      </div>
    );
  };

  // Render market share bar
  const renderMarketShare = (share) => {
    return (
      <div className="market-share-container">
        <div className="market-share-bar">
          <div
            className="market-share-fill"
            style={{ width: `${Math.min(share, 100)}%` }}
          />
        </div>
        <span className="market-share-label">{share}%</span>
      </div>
    );
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Render company card
  const renderCompanyCard = (company) => {
    const marketShare = company.marketShare?.[selectedBusinessType] || 0;
    const { volume, quality } = company.powerRating || { volume: 3, quality: 3 };
    const IconComponent = PLATFORM_ICONS[company.id] || FiBox;

    return (
      <div
        key={company.id}
        className={`company-card ${company.isEssential ? 'essential' : ''}`}
        onClick={() => setSelectedCompany(company)}
      >
        {company.isEssential && (
          <div className="essential-badge">Essential</div>
        )}
        {company.hasWarning && (
          <div className="warning-badge" title={company.warningText}>
            <FiAlertCircle size={14} />
          </div>
        )}
        <div className="card-header">
          <div className="company-icon">
            <IconComponent size={24} />
          </div>
          <div className="company-info">
            <h4 className="company-name">{company.name}</h4>
            {renderStars(volume, quality)}
          </div>
        </div>
        <p className="company-oneliner">{company.oneLiner}</p>
        {renderMarketShare(marketShare)}
      </div>
    );
  };

  // Render detail popup
  const renderDetailPopup = () => {
    if (!selectedCompany) return null;

    const { deepDive, marketShare, powerRating } = selectedCompany;
    const share = marketShare?.[selectedBusinessType] || 0;
    const PopupIcon = PLATFORM_ICONS[selectedCompany.id] || FiBox;

    return (
      <div className="detail-overlay" onClick={() => setSelectedCompany(null)}>
        <div className="detail-popup" onClick={e => e.stopPropagation()}>
          <button className="close-btn" onClick={() => setSelectedCompany(null)}>
            <FiX size={24} />
          </button>

          {/* Header */}
          <div className="popup-header">
            <div className="popup-icon">
              <PopupIcon size={36} />
            </div>
            <div className="popup-title-section">
              <h2>{selectedCompany.name}</h2>
              <p className="popup-oneliner">{selectedCompany.oneLiner}</p>
              {selectedCompany.hasWarning && (
                <div className="popup-warning">
                  <FiAlertCircle size={16} />
                  {selectedCompany.warningText}
                </div>
              )}
            </div>
            <div className="popup-meta">
              <div className="meta-item">
                <span className="meta-label">Market Share ({BUSINESS_TYPES.find(b => b.id === selectedBusinessType)?.label})</span>
                <span className="meta-value">{share}%</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Power Rating</span>
                <div className="meta-stars">
                  {renderStars(powerRating?.volume || 3, powerRating?.quality || 3)}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="popup-content">
            {/* Overview Section */}
            <div className="content-section">
              <div
                className="section-header clickable"
                onClick={() => toggleSection('overview')}
              >
                <h3>Overview</h3>
                {expandedSections.overview ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {(expandedSections.overview !== false) && (
                <div className="section-body">
                  <p className="overview-text">{deepDive?.overview}</p>
                </div>
              )}
            </div>

            {/* VC Perspective - Highlighted */}
            {deepDive?.vcPerspective && (
              <div className="content-section vc-section">
                <div
                  className="section-header clickable"
                  onClick={() => toggleSection('vcPerspective')}
                >
                  <h3>VC Perspective</h3>
                  {expandedSections.vcPerspective ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {(expandedSections.vcPerspective !== false) && (
                  <div className="section-body">
                    <p className="vc-text">{deepDive.vcPerspective}</p>
                  </div>
                )}
              </div>
            )}

            {/* Transcend Alpha - Premium Insight */}
            {deepDive?.transcendAlpha && (
              <div className="content-section alpha-section">
                <div
                  className="section-header clickable"
                  onClick={() => toggleSection('transcendAlpha')}
                >
                  <h3>Transcend Alpha</h3>
                  {expandedSections.transcendAlpha ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {(expandedSections.transcendAlpha !== false) && (
                  <div className="section-body">
                    <p className="alpha-text">{deepDive.transcendAlpha}</p>
                  </div>
                )}
              </div>
            )}

            {/* Pros & Cons */}
            <div className="content-section">
              <div
                className="section-header clickable"
                onClick={() => toggleSection('proscons')}
              >
                <h3>Pros & Cons</h3>
                {expandedSections.proscons ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {(expandedSections.proscons !== false) && (
                <div className="section-body pros-cons">
                  <div className="pros">
                    <h4>Pros</h4>
                    <ul>
                      {deepDive?.pros?.map((pro, i) => (
                        <li key={i}>{pro}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="cons">
                    <h4>Cons</h4>
                    <ul>
                      {deepDive?.cons?.map((con, i) => (
                        <li key={i}>{con}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Data Flow */}
            {(deepDive?.upstream || deepDive?.downstream) && (
              <div className="content-section">
                <div
                  className="section-header clickable"
                  onClick={() => toggleSection('dataflow')}
                >
                  <h3>Data Flow</h3>
                  {expandedSections.dataflow ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {(expandedSections.dataflow !== false) && (
                  <div className="section-body data-flow">
                    {deepDive?.upstream && (
                      <div className="flow-item">
                        <span className="flow-label">Upstream</span>
                        <p>{deepDive.upstream}</p>
                      </div>
                    )}
                    {deepDive?.downstream && (
                      <div className="flow-item">
                        <span className="flow-label">Downstream</span>
                        <p>{deepDive.downstream}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Details Grid */}
            <div className="content-section">
              <div
                className="section-header clickable"
                onClick={() => toggleSection('details')}
              >
                <h3>Details</h3>
                {expandedSections.details ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {(expandedSections.details !== false) && (
                <div className="section-body details-grid">
                  {deepDive?.integrationDifficulty && (
                    <div className="detail-item">
                      <span className="detail-label">Integration Difficulty</span>
                      <div className="difficulty-dots">
                        {[1, 2, 3, 4, 5].map(i => (
                          <span
                            key={i}
                            className={`dot ${i <= deepDive.integrationDifficulty ? 'filled' : ''}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {deepDive?.pricingModel && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Pricing Model</span>
                      <p className="detail-value">{deepDive.pricingModel}</p>
                    </div>
                  )}
                  {deepDive?.bestFor && deepDive.bestFor.length > 0 && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Best For</span>
                      <div className="best-for-tags">
                        {deepDive.bestFor.map(type => {
                          const businessType = BUSINESS_TYPES.find(b => b.id === type);
                          if (!businessType) return null;
                          const BestForIcon = businessType.IconComponent;
                          return (
                            <span
                              key={type}
                              className="best-for-tag"
                              style={{ backgroundColor: `${businessType.color}20`, color: businessType.color }}
                            >
                              <BestForIcon size={14} /> {businessType.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {deepDive?.competitors && deepDive.competitors.length > 0 && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Competitors</span>
                      <div className="competitors-list">
                        {deepDive.competitors.map(compId => {
                          const comp = AD_TECH_PLATFORMS[compId];
                          if (!comp) return null;
                          const CompIcon = PLATFORM_ICONS[compId] || FiBox;
                          return (
                            <span
                              key={compId}
                              className="competitor-tag"
                              onClick={() => {
                                const fullCompany = AD_TECH_COMPANIES.find(c => c.id === compId);
                                if (fullCompany) setSelectedCompany(fullCompany);
                              }}
                            >
                              <CompIcon size={14} /> {comp.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ad-tech-navigator">
      {/* Header */}
      <header className="navigator-header">
        <div className="header-content">
          <h1>Ad Tech Value Chain Navigator</h1>
          <p>Interactive map of the mobile ad tech ecosystem. Select a business type to see relevant platforms.</p>
        </div>
      </header>

      {/* Business Type Selector */}
      <div className="business-type-selector">
        {BUSINESS_TYPES.map(type => {
          const TypeIcon = type.IconComponent;
          return (
            <button
              key={type.id}
              className={`type-btn ${selectedBusinessType === type.id ? 'active' : ''}`}
              onClick={() => setSelectedBusinessType(type.id)}
              style={{
                '--type-color': type.color,
                borderColor: selectedBusinessType === type.id ? type.color : 'transparent'
              }}
            >
              <TypeIcon className="type-icon" size={18} />
              <span className="type-label">{type.label}</span>
            </button>
          );
        })}
      </div>

      {/* Value Chain Columns */}
      <div className="value-chain">
        {CATEGORIES.map(category => {
          const companies = getCompaniesForCategory(category.id);
          if (companies.length === 0) return null;

          return (
            <div key={category.id} className="chain-column">
              <div
                className="column-header"
                style={{ backgroundColor: category.color }}
              >
                <h3>{category.label}</h3>
                <span className="column-count">{companies.length}</span>
              </div>
              <p className="column-description">{category.description}</p>
              <div className="column-cards">
                {companies.map(company => renderCompanyCard(company))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Flow Arrows */}
      <div className="flow-indicator">
        <FiArrowRight className="flow-arrow" />
        <span className="flow-text">Data & Attribution Flow</span>
        <FiArrowRight className="flow-arrow" />
      </div>

      {/* Detail Popup */}
      {renderDetailPopup()}
    </div>
  );
}

export default AdTechNavigator;
